/**
 * Firestore Service for Editor
 * Firestore에서 에디터 문서 데이터를 불러오기 + 프로젝트 저장/로드
 */
import { db } from '../../firebase';
import {
  doc, getDoc, setDoc, deleteDoc, serverTimestamp,
  collection, getDocs, query, orderBy, where,
} from 'firebase/firestore';

// ========== EDITOR_DOCS (템플릿 - 파서에서 생성된 문서) ==========

/**
 * 모든 에디터 문서 목록 로드 (created_at 내림차순)
 */
export const loadAllEditorDocuments = async () => {
  try {
    const docsRef = collection(db, 'EDITOR_DOCS');
    const docsQuery = query(docsRef, orderBy('created_at', 'desc'));
    const docsSnap = await getDocs(docsQuery);

    const documents = docsSnap.docs.map(docSnap => ({
      id: docSnap.id,
      ...docSnap.data(),
    }));

    return { success: true, documents };
  } catch (error) {
    console.error('문서 목록 로드 오류:', error);
    return { success: false, error: error.message };
  }
};

/**
 * 테이블 레이어의 flat_cells → 2D cells 복원
 */
const restoreTableCells = (layerData) => {
  if (layerData.type !== 'table' || !layerData.flat_cells || layerData.cells) return;
  const rows = layerData.rows || 1;
  const cols = layerData.cols || 1;
  const cells = [];
  for (let r = 0; r < rows; r++) {
    const rowData = [];
    for (let c = 0; c < cols; c++) {
      rowData.push(layerData.flat_cells[r * cols + c]);
    }
    cells.push(rowData);
  }
  layerData.cells = cells;
  delete layerData.flat_cells;
};

/**
 * 레이어 문서 목록을 파싱하여 배열로 변환
 */
const parseLayers = (layersSnap, useThumbnail) => {
  return layersSnap.docs
    .map(layerDoc => {
      const layerData = { id: layerDoc.id, ...layerDoc.data() };
      if (useThumbnail && layerData.type === 'image' && layerData.image_thumb_url) {
        layerData.image_url = layerData.image_thumb_url;
      }
      restoreTableCells(layerData);
      return layerData;
    })
    .sort((a, b) => a.id.localeCompare(b.id));
};

/**
 * 에디터 문서 로드
 */
export const loadEditorDocument = async (docId, options = {}) => {
  const { useThumbnail = false } = options;

  try {
    const docRef = doc(db, 'EDITOR_DOCS', docId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      return { success: false, error: '문서를 찾을 수 없습니다.' };
    }

    const docData = docSnap.data();
    const slidesRef = collection(db, 'EDITOR_DOCS', docId, 'slides');
    const slidesSnap = await getDocs(query(slidesRef, orderBy('page')));

    const slides = [];
    for (const slideDoc of slidesSnap.docs) {
      const layersRef = collection(db, 'EDITOR_DOCS', docId, 'slides', slideDoc.id, 'layers');
      const layersSnap = await getDocs(layersRef);
      const layers = parseLayers(layersSnap, useThumbnail);
      slides.push({ ...slideDoc.data(), id: slideDoc.id, layers });
    }

    return {
      success: true,
      data: {
        id: docId,
        canvas_size: [docData.canvas_size_x, docData.canvas_size_y],
        file_type: docData.file_type,
        status: docData.status,
        created_at: docData.created_at,
        userId: docData.userId,
        slide_count: docData.slide_count,
        slides,
      },
    };
  } catch (error) {
    console.error('문서 로드 오류:', error);
    return { success: false, error: error.message };
  }
};

/**
 * 특정 슬라이드의 레이어 데이터 로드
 */
export const loadSlideData = async (docId, slideIndex, options = {}) => {
  const { useThumbnail = false } = options;

  try {
    const slideId = String(slideIndex).padStart(5, '0');
    const layersRef = collection(db, 'EDITOR_DOCS', docId, 'slides', slideId, 'layers');
    const layersSnap = await getDocs(layersRef);
    const layers = parseLayers(layersSnap, useThumbnail);

    return { success: true, layers };
  } catch (error) {
    console.error('슬라이드 데이터 로드 오류:', error);
    return { success: false, error: error.message };
  }
};

// ========== EDITOR_PROJECTS (유저 저장 프로젝트) ==========

const PROJECTS_COLLECTION = 'EDITOR_PROJECTS';
const CHUNK_SIZE = 800_000; // 800KB per chunk

/**
 * canvasState가 크면 chunks 하위 컬렉션에 분할 저장
 */
const saveSlideState = async (projectId, slideIndex, canvasState) => {
  const slideId = String(slideIndex).padStart(5, '0');
  const slideRef = doc(db, PROJECTS_COLLECTION, projectId, 'slides', slideId);
  const chunksRef = collection(db, PROJECTS_COLLECTION, projectId, 'slides', slideId, 'chunks');

  // 기존 chunks 삭제
  const oldChunks = await getDocs(chunksRef);
  for (const chunkDoc of oldChunks.docs) {
    await deleteDoc(chunkDoc.ref);
  }

  if (!canvasState || canvasState.length <= CHUNK_SIZE) {
    await setDoc(slideRef, { page: slideIndex, canvasState: canvasState || null, chunked: false });
  } else {
    const totalChunks = Math.ceil(canvasState.length / CHUNK_SIZE);
    for (let c = 0; c < totalChunks; c++) {
      const chunkId = String(c).padStart(4, '0');
      const chunkData = canvasState.slice(c * CHUNK_SIZE, (c + 1) * CHUNK_SIZE);
      const chunkRef = doc(db, PROJECTS_COLLECTION, projectId, 'slides', slideId, 'chunks', chunkId);
      await setDoc(chunkRef, { index: c, data: chunkData });
    }
    await setDoc(slideRef, { page: slideIndex, canvasState: null, chunked: true, chunkCount: totalChunks });
  }
};

/**
 * 슬라이드의 canvasState 로드 (청크 분할된 경우 재조립)
 */
const loadSlideState = async (projectId, slideDoc) => {
  const slideData = slideDoc.data();
  if (!slideData.chunked) return slideData.canvasState || null;

  const chunksRef = collection(slideDoc.ref, 'chunks');
  const chunksSnap = await getDocs(query(chunksRef, orderBy('index')));

  let canvasState = '';
  for (const chunkDoc of chunksSnap.docs) {
    canvasState += chunkDoc.data().data;
  }
  return canvasState || null;
};

/**
 * 프로젝트 저장
 */
export const saveProject = async ({ projectId, forcedId, name, canvasSize, backgroundColor, userId, slidesData }) => {
  try {
    const isNew = !projectId;
    const id = projectId || forcedId || doc(collection(db, PROJECTS_COLLECTION)).id;
    const projectRef = doc(db, PROJECTS_COLLECTION, id);

    const projectData = {
      name: name || '새 프로젝트',
      canvas_size_x: canvasSize.width,
      canvas_size_y: canvasSize.height,
      backgroundColor: backgroundColor || '#ffffff',
      userId: userId || null,
      slide_count: slidesData.length || 1,
      updated_at: serverTimestamp(),
    };

    if (isNew) {
      projectData.created_at = serverTimestamp();
    }

    await setDoc(projectRef, projectData, { merge: true });

    for (let i = 0; i < slidesData.length; i++) {
      await saveSlideState(id, i, slidesData[i].canvasState);
    }

    return { success: true, projectId: id };
  } catch (error) {
    console.error('프로젝트 저장 오류:', error);
    return { success: false, error: error.message };
  }
};

/**
 * 프로젝트 로드
 */
export const loadProject = async (projectId) => {
  try {
    const projectRef = doc(db, PROJECTS_COLLECTION, projectId);
    const projectSnap = await getDoc(projectRef);

    if (!projectSnap.exists()) {
      return { success: false, error: '프로젝트를 찾을 수 없습니다.' };
    }

    const projectData = projectSnap.data();
    const slidesRef = collection(db, PROJECTS_COLLECTION, projectId, 'slides');
    const slidesSnap = await getDocs(query(slidesRef, orderBy('page')));

    const slides = [];
    for (const slideDoc of slidesSnap.docs) {
      const canvasState = await loadSlideState(projectId, slideDoc);
      slides.push({ id: slideDoc.id, ...slideDoc.data(), canvasState });
    }

    return {
      success: true,
      data: {
        id: projectId,
        name: projectData.name,
        canvasSize: { width: projectData.canvas_size_x, height: projectData.canvas_size_y },
        backgroundColor: projectData.backgroundColor || '#ffffff',
        userId: projectData.userId,
        slide_count: projectData.slide_count,
        created_at: projectData.created_at,
        updated_at: projectData.updated_at,
        slides,
      },
    };
  } catch (error) {
    console.error('프로젝트 로드 오류:', error);
    return { success: false, error: error.message };
  }
};

/**
 * 모든 프로젝트 목록 로드 (updated_at 내림차순)
 */
export const loadAllProjects = async (userId = null) => {
  try {
    const projectsRef = collection(db, PROJECTS_COLLECTION);
    const projectsQuery = userId
      ? query(projectsRef, where('userId', '==', userId), orderBy('updated_at', 'desc'))
      : query(projectsRef, orderBy('updated_at', 'desc'));

    const projectsSnap = await getDocs(projectsQuery);
    const projects = projectsSnap.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));

    return { success: true, projects };
  } catch (error) {
    console.error('프로젝트 목록 로드 오류:', error);
    return { success: false, error: error.message };
  }
};
